import { z } from 'zod';

/**
 * Голос (§10, §29-30).
 *
 * Отдельной обязательной записи голоса в онбординге нет (§80): голос создаётся
 * из аудиодорожки основной записи. Дополнительная запись — только улучшение.
 */

export const VoiceStatus = {
  NOT_STARTED: 'not_started',
  ANALYZING: 'analyzing',
  CLONING: 'cloning',
  READY: 'ready',
  WEAK: 'weak',
  FAILED: 'failed',
} as const;
export type VoiceStatus = (typeof VoiceStatus)[keyof typeof VoiceStatus];

export const voiceStatusLabels: Record<VoiceStatus, string> = {
  not_started: 'Не создан',
  analyzing: 'Разбираем запись',
  cloning: 'Создаётся',
  ready: 'Готов',
  weak: 'Готов',
  failed: 'Не удалось создать',
};

/** §10: «Качество: Хорошее». Оценка словами, а не числом. */
export type VoiceQuality = 'good' | 'fair' | 'weak';

export const voiceQualityLabels: Record<VoiceQuality, string> = {
  good: 'Хорошее',
  fair: 'Среднее',
  weak: 'Слабое',
};

/** Чего не хватило записи. По этому подбирается текст улучшения (§10.2). */
export const VoiceDeficit = {
  SHORT: 'short',
  NOISY: 'noisy',
  QUIET: 'quiet',
  MONOTONE: 'monotone',
  NO_QUESTIONS: 'no_questions',
  FEW_SEGMENTS: 'few_segments',
} as const;
export type VoiceDeficit = (typeof VoiceDeficit)[keyof typeof VoiceDeficit];

export const voiceDeficitLabels: Record<VoiceDeficit, string> = {
  short: 'мало речи',
  noisy: 'фоновый шум',
  quiet: 'тихая запись',
  monotone: 'ровная интонация',
  no_questions: 'нет вопросительных фраз',
  few_segments: 'мало разных фраз',
};

export interface VoiceProfileView {
  id: string;
  status: VoiceStatus;
  label: string;
  quality: VoiceQuality | null;
  qualityLabel: string | null;
  /** Что улучшить. Пустой список — улучшать нечего. */
  deficits: VoiceDeficit[];
  /** Показывать ли предложение дополнить запись (§10.1). */
  improvementSuggested: boolean;
  /** Сколько примерно ещё надо начитать. */
  improvementSec: number;
  previewUrl: string | null;
  language: string;
  errorCode: string | null;
  createdAt: string;
  readyAt: string | null;
}

export const synthesizeSchema = z
  .object({
    text: z.string().trim().min(1).max(1000),
    /** Разметка §30 — адаптер игнорирует её, если провайдер не умеет. */
    emotion: z
      .enum(['neutral', 'confident', 'warm', 'excited', 'serious', 'calm', 'questioning'])
      .optional(),
  })
  .strict();

export const improveVoiceSchema = z
  .object({
    assetId: z.string().min(1),
    scriptVersion: z.string().min(1).optional(),
  })
  .strict();

/** Эмоции §30. Передаются провайдеру, если он поддерживает управление стилем. */
export const VoiceEmotion = {
  NEUTRAL: 'neutral',
  CONFIDENT: 'confident',
  WARM: 'warm',
  EXCITED: 'excited',
  SERIOUS: 'serious',
  CALM: 'calm',
  QUESTIONING: 'questioning',
} as const;
export type VoiceEmotion = (typeof VoiceEmotion)[keyof typeof VoiceEmotion];

export const voiceEmotionLabels: Record<VoiceEmotion, string> = {
  neutral: 'Нейтрально',
  confident: 'Уверенно',
  warm: 'Тепло',
  excited: 'С воодушевлением',
  serious: 'Серьёзно',
  calm: 'Спокойно',
  questioning: 'Вопросительно',
};
